import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UavDataTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/uavDataType.service';
import { TagService } from '@6grain/masofa-monolith-api-client/dictionaries/api/tag.service';
import { UavCameraTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/uavCameraType.service';
import { UavDataType } from '@6grain/masofa-monolith-api-client/dictionaries/model/uavDataType';
import { Tag } from '@6grain/masofa-monolith-api-client/dictionaries/model/tag';
import { UavCameraType } from '@6grain/masofa-monolith-api-client/dictionaries/model/uavCameraType';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { TagSelectorComponent } from '../tag-selector/tag-selector.component';
import { ConfirmationModalComponent } from '../../Ui-Kit/confirmation-modal/confirmation-modal.component';
import { environment } from '@root/src/environments/environment';
import { Subscription, timer } from 'rxjs';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';

@Component({
  selector: 'app-uav-upload-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ServerTranslatePipe,
    TagSelectorComponent,
    ConfirmationModalComponent,
    PrimaryButton,
    SecondaryButton,
  ],
  templateUrl: './uav-upload-modal.component.html',
  styleUrls: ['./uav-upload-modal.component.scss'],
})
export class UavUploadModalComponent implements OnInit, OnDestroy {
  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() uploadSuccess = new EventEmitter<void>();

  uploadForm!: FormGroup;
  selectedFile: File | null = null;
  isSubmitting: boolean = false;
  uploadError: string | null = null;
  uploadProgress: number = 0;
  estimatedProcessingTime: number = 0;
  remainingTime: number = 0;
  private readonly PROCESSING_COEFFICIENT = 0.8 / (1024 * 1024);
  private timerSubscription: Subscription | null = null;

  isCancelConfirmVisible: boolean = false;

  uavDataTypes: UavDataType[] = [];
  cameraTypes: UavCameraType[] = [];
  tags: Tag[] = [];

  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private uavDataTypeService = inject(UavDataTypeService);
  private tagService = inject(TagService);
  private uavCameraTypeService = inject(UavCameraTypeService);
  private translate = inject(TranslateService);

  private uploadSubscription: Subscription | null = null;

  constructor() {}

  ngOnInit(): void {
    this.initForm();
    this.loadDictionaries();
  }

  ngOnDestroy(): void {
    this.stopProcessingTimer();
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
  }

  private initForm(): void {
    this.uploadForm = this.fb.group({
      dataType: [null],
      cameraType: [null],
      tags: [[]],
      analysisOnly: [true],
      comment: [''],
    });
  }

  onTagCreated(newTag: Tag): void {
    this.tags = [...this.tags, newTag];
  }

  private loadDictionaries(): void {
    this.uavDataTypeService
      .dictionariesUavDataTypeGetByQueryPost({})
      .subscribe({
        next: (res) => {
          this.uavDataTypes = res || [];
        },
        error: (err) => console.error('Error loading UAV Data Types:', err),
      });
    this.uavCameraTypeService
      .dictionariesUavCameraTypeGetByQueryPost({})
      .subscribe({
        next: (res) => {
          this.cameraTypes = res || [];
        },
        error: (err) => console.error('Error loading UAV Camera Types:', err),
      });
    this.tagService.dictionariesTagGetAllGet().subscribe({
      next: (res) => {
        this.tags = (res as unknown as Tag[]) || [];
      },
      error: (err) => console.error('Error loading Tags:', err),
    });
  }

  onClose(): void {
    if (this.isSubmitting) {
      this.isCancelConfirmVisible = true;
    } else {
      this.forceClose();
    }
  }

  forceClose(): void {
    this.stopProcessingTimer();

    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }

    this.isCancelConfirmVisible = false;
    this.close.emit();
    this.uploadForm.reset({ analysisOnly: true });
    this.selectedFile = null;
    this.isSubmitting = false;
    this.uploadProgress = 0;
    this.uploadError = null;
  }

  closeCancelConfirm(): void {
    this.isCancelConfirmVisible = false;
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    const file = element.files?.[0];
    if (file) {
      this.selectedFile = file;
      this.uploadError = null;
      this.calculateEstimatedTime(file.size);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  async onFileDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
      this.uploadError = null;
      this.calculateEstimatedTime(this.selectedFile.size);
    }
  }

  private calculateEstimatedTime(bytes: number): void {
    this.estimatedProcessingTime = Math.ceil(
      bytes * this.PROCESSING_COEFFICIENT
    );
    if (this.estimatedProcessingTime < 5) {
      this.estimatedProcessingTime = 5;
    }
    this.remainingTime = this.estimatedProcessingTime;
  }

  private startProcessingTimer(): void {
    this.stopProcessingTimer();
    this.timerSubscription = timer(0, 1000).subscribe(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
      }
    });
  }

  private stopProcessingTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  onUploadSubmit(): void {
    this.uploadForm.markAllAsTouched();
    if (!this.selectedFile) {
      this.uploadError = this.translate.instant('ERROR_FILE_REQUIRED');
      return;
    }
    this.isSubmitting = true;
    this.uploadError = null;
    this.uploadProgress = 0;
    this.remainingTime = this.estimatedProcessingTime;
    const formValues = this.uploadForm.value;
    const formData = new FormData();
    formData.append('ZipFile', this.selectedFile);
    if (formValues.comment) {
      formData.append('Comment', formValues.comment);
    }
    if (formValues.dataType) {
      formData.append('DataTypeId', formValues.dataType);
    }
    if (formValues.cameraType) {
      formData.append('CameraTypeId', formValues.cameraType);
    }
    formData.append('AnalysisOnly', formValues.analysisOnly ? 'true' : 'false');
    if (
      formValues.tags &&
      Array.isArray(formValues.tags) &&
      formValues.tags.length > 0
    ) {
      formValues.tags.forEach((tag: any) => {
        const val = tag.id ? tag.id : tag;
        formData.append('TagIds', val);
      });
    }
    const uploadUrl = `${environment.api}/uav/UavPhoto/UploadSurveyArchive`;
    const req = new HttpRequest('POST', uploadUrl, formData, {
      reportProgress: true,
      responseType: 'json',
    });

    this.uploadSubscription = this.http.request(req).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total) {
            this.uploadProgress = Math.round(
              (100 * event.loaded) / event.total
            );
            if (this.uploadProgress === 100 && !this.timerSubscription) {
              this.startProcessingTimer();
            }
          }
        } else if (event.type === HttpEventType.Response) {
          this.stopProcessingTimer();
          this.isSubmitting = false;
          this.uploadSubscription = null;
          this.uploadSuccess.emit();
          this.onClose();
        }
      },
      error: (err) => {
        console.error('Upload error', err);
        this.stopProcessingTimer();
        this.isSubmitting = false;
        this.uploadProgress = 0;
        this.uploadError = this.translate.instant('Upload failed');
        this.uploadSubscription = null;
      },
    });
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
